/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { EnrollActivityTaskReq } from './enroll-activity-task-req'
import { EnrollActivityTask } from './enroll-activity-task'
import { SubmitEnrollReq } from './submit-enroll-req'
import { TaskReflection } from './task-reflection'
import { LoadEnrollActivityTaskQuery } from './load-enroll-activity-task-query'
import { QueryEnrollTaskReflectionsQuery } from './query-enroll-task-reflections-query'
import { ExportEnrollTaskReflectionsQuery } from './export-enroll-task-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const enrollApi = {
  createEnrollActivityTaskConfig(
    enrollActivityTaskReq: EnrollActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/enroll/activity-tasks',
      {},
      null,
      enrollActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          options: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】报名-创建活动任务
   * @param enrollActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<EnrollActivityTask>
   */
  createEnrollActivityTask(
    enrollActivityTaskReq: EnrollActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<EnrollActivityTask> {
    return axios(
      enrollApi.createEnrollActivityTaskConfig(enrollActivityTaskReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new EnrollActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateEnrollActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: EnrollActivityTaskReq) =>
        enrollApi.createEnrollActivityTask(payload),
      mutationOptions
    )
  },

  loadEnrollActivityTaskConfig(
    id: number,
    loadEnrollActivityTaskQuery?: LoadEnrollActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/enroll/activity-tasks/{id}',
      {
        id,
      },
      loadEnrollActivityTaskQuery
    )
  },

  /**
   * @summary 【前/后台】报名-查看活动任务
   *  - 后台：活动任务+报名选项+完成任务用户数量
   *  - 前台：活动任务+报名选项+活动任务完成情况
   * @param id
   * @param [loadEnrollActivityTaskQuery]
   * @param [loadEnrollActivityTaskQuery.expand] expand
   *  - 活动任务关联的活动人员 task.task_reflections
   *  - 活动任务关联的管理人员 task.admin_reflections
   * @param [loadEnrollActivityTaskQuery.isForeground] 是否是前台请求
   *  - 是：返回-活动任务完成情况
   *  - 否：不返回-活动任务完成情况
   * @param [options]
   * @return AxiosResponsePromise<EnrollActivityTask>
   */
  loadEnrollActivityTask<TQuery extends LoadEnrollActivityTaskQuery>(
    id: number,
    loadEnrollActivityTaskQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<EnrollActivityTask, TQuery['expand']>> {
    return axios(
      enrollApi.loadEnrollActivityTaskConfig(
        id,
        loadEnrollActivityTaskQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new EnrollActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadEnrollActivityTask<TQuery extends LoadEnrollActivityTaskQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<EnrollActivityTask, TQuery['expand']>> {
    return useQueryApi<Expand<EnrollActivityTask, TQuery['expand']>>(
      enrollApi.loadEnrollActivityTask,
      'Enroll.loadEnrollActivityTask',
      queryOptions,
      id,
      query
    )
  },

  updateEnrollActivityTaskConfig(
    id: number,
    enrollActivityTaskReq: EnrollActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/enroll/activity-tasks/{id}',
      {
        id,
      },
      null,
      enrollActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          options: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】报名-编辑活动任务
   *  - 报名选项：新增、删除、编辑（将清空当前用户任务完成数据；用户完成状态）
   *  - 已有用户完成任务：不允许编辑开启方式
   * @param id
   * @param enrollActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<EnrollActivityTask>
   */
  updateEnrollActivityTask(
    id: number,
    enrollActivityTaskReq: EnrollActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<EnrollActivityTask> {
    return axios(
      enrollApi.updateEnrollActivityTaskConfig(
        id,
        enrollActivityTaskReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new EnrollActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateEnrollActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: EnrollActivityTaskReq }) =>
        enrollApi.updateEnrollActivityTask(id, payload),
      mutationOptions
    )
  },

  deleteEnrollActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/enroll/activity-tasks/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】报名-删除活动任务
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteEnrollActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(enrollApi.deleteEnrollActivityTaskConfig(id, options)) as any
  },

  useDeleteEnrollActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => enrollApi.deleteEnrollActivityTask(id),
      mutationOptions
    )
  },

  modifyEnrollTaskCompletedConfig(
    id: number,
    submitEnrollReq: SubmitEnrollReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/enroll/activity-tasks/{id}/completed',
      {
        id,
      },
      null,
      submitEnrollReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【前台】报名-完成报名任务
   *    - 如用户不在报名任务人员列表内，将报错
   * @param id
   * @param submitEnrollReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  modifyEnrollTaskCompleted(
    id: number,
    submitEnrollReq: SubmitEnrollReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      enrollApi.modifyEnrollTaskCompletedConfig(id, submitEnrollReq, options)
    ) as any
  },

  useModifyEnrollTaskCompleted(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: SubmitEnrollReq }) =>
        enrollApi.modifyEnrollTaskCompleted(id, payload),
      mutationOptions
    )
  },

  queryEnrollTaskReflectionsConfig(
    id: number,
    queryEnrollTaskReflectionsQuery?: QueryEnrollTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/enroll/activity-tasks/{id}/reflections',
      {
        id,
      },
      queryEnrollTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】测验任务-测验数据-任务人员列表(完成情况)
   * @param id
   * @param [queryEnrollTaskReflectionsQuery]
   * @param [queryEnrollTaskReflectionsQuery.expand]
   * @param [queryEnrollTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryEnrollTaskReflectionsQuery.reflection.nameIn]
   * @param [queryEnrollTaskReflectionsQuery.reflection.nameLike]
   * @param [queryEnrollTaskReflectionsQuery.reflection.accountLike]
   * @param [queryEnrollTaskReflectionsQuery.reflection.accountIn]
   * @param [queryEnrollTaskReflectionsQuery.reflection.genderIn]
   * @param [queryEnrollTaskReflectionsQuery.reflection.statusIn]
   * @param [queryEnrollTaskReflectionsQuery.reflection.phoneIn]
   * @param [queryEnrollTaskReflectionsQuery.reflection.phoneLike]
   * @param [queryEnrollTaskReflectionsQuery.reflection.emailIn]
   * @param [queryEnrollTaskReflectionsQuery.reflection.emailLike]
   * @param [queryEnrollTaskReflectionsQuery.enrollOptionIds] 根据报名任务选项筛选
   * @param [queryEnrollTaskReflectionsQuery.sort]
   * @param [queryEnrollTaskReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryEnrollTaskReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryEnrollTaskReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [queryEnrollTaskReflectionsQuery.startAtEgt] 开始时间-大于
   * @param [queryEnrollTaskReflectionsQuery.startAtElt] 开始时间-小于
   * @param [queryEnrollTaskReflectionsQuery.endAtEgt] 完成时间-大于
   * @param [queryEnrollTaskReflectionsQuery.endAtElt] 完成时间-小于
   * @param [queryEnrollTaskReflectionsQuery.completedStatus] 完成状态
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryEnrollTaskReflections<TQuery extends QueryEnrollTaskReflectionsQuery>(
    id: number,
    queryEnrollTaskReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<TaskReflection, TQuery['expand']>[]> {
    return axios(
      enrollApi.queryEnrollTaskReflectionsConfig(
        id,
        queryEnrollTaskReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryEnrollTaskReflections<TQuery extends QueryEnrollTaskReflectionsQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<TaskReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<TaskReflection, TQuery['expand']>[]>(
      enrollApi.queryEnrollTaskReflections,
      'Enroll.queryEnrollTaskReflections',
      queryOptions,
      id,
      query
    )
  },

  exportEnrollTaskReflectionsConfig(
    id: number,
    exportEnrollTaskReflectionsQuery?: ExportEnrollTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/enroll/activity-tasks/{id}/reflections/export',
      {
        id,
      },
      exportEnrollTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】报名任务-报名数据-任务人员列表(完成情况)
   * @param id
   * @param [exportEnrollTaskReflectionsQuery]
   * @param [exportEnrollTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [exportEnrollTaskReflectionsQuery.reflection.nameIn]
   * @param [exportEnrollTaskReflectionsQuery.reflection.nameLike]
   * @param [exportEnrollTaskReflectionsQuery.reflection.accountLike]
   * @param [exportEnrollTaskReflectionsQuery.reflection.accountIn]
   * @param [exportEnrollTaskReflectionsQuery.reflection.genderIn]
   * @param [exportEnrollTaskReflectionsQuery.reflection.statusIn]
   * @param [exportEnrollTaskReflectionsQuery.reflection.phoneIn]
   * @param [exportEnrollTaskReflectionsQuery.reflection.phoneLike]
   * @param [exportEnrollTaskReflectionsQuery.reflection.emailIn]
   * @param [exportEnrollTaskReflectionsQuery.reflection.emailLike]
   * @param [exportEnrollTaskReflectionsQuery.enrollOptionIds] 根据报名任务选项筛选
   * @param [exportEnrollTaskReflectionsQuery.filename] 不带后缀文件名
   * @param [exportEnrollTaskReflectionsQuery.ridIn]
   * @param [exportEnrollTaskReflectionsQuery.startAtEgt] 开始时间-大于
   * @param [exportEnrollTaskReflectionsQuery.startAtElt] 开始时间-小于
   * @param [exportEnrollTaskReflectionsQuery.endAtEgt] 完成时间-大于
   * @param [exportEnrollTaskReflectionsQuery.endAtElt] 完成时间-小于
   * @param [exportEnrollTaskReflectionsQuery.completedStatus] 完成状态
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  exportEnrollTaskReflections(
    id: number,
    exportEnrollTaskReflectionsQuery?: ExportEnrollTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      enrollApi.exportEnrollTaskReflectionsConfig(
        id,
        exportEnrollTaskReflectionsQuery,
        options
      )
    ) as any
  },

  useExportEnrollTaskReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: ExportEnrollTaskReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      enrollApi.exportEnrollTaskReflections,
      'Enroll.exportEnrollTaskReflections',
      queryOptions,
      id,
      query
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(enrollApi).forEach((api) => {
  // @ts-ignore
  enrollApi[api]._name_ = `Enroll.${api}`
})
